// Unpublished Work © 2026 Deere & Company.

import {
    dataTableFormatTime
} from 'Utils/time-utils';
import accounting from 'accounting';
import moment from 'moment';
import {alphaNumericCompare, defaultSortMethod} from 'Ui/models/maintenance';
import {INVENTORIES} from 'Ui/components/graph/constants/graph-filters';
import {replaceTranslationNames} from 'Utils/translation-utils';
import {UNITS} from 'Common/constants/units/unit-config-constants';
import {formatNumber} from 'Utils/unit-conversion-utils';

function formatTwoDecimalPlaces(value) {
    return formatNumber(value, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
}

export function getEquipmentCostExportContent(rollupData, translations) {
    const {
        dateChildren, ...totals
    } = rollupData;
    const header = [
        translations.DATE,
        translations.ONLINK_PURCHASE,
        translations.ONLINK_PROPERTY_TAX,
        translations.ONLINK_SALES_TAX,
        translations.PARTS,
        translations.ONLINK_SERVICE,
        translations.ONLINK_TOTAL
    ];
    const rows = [...dateChildren || []]
        .sort((a, b) => alphaNumericCompare(a.name, b.name))
        .map((row) => [
            dataTableFormatTime(row.dateSelect, row.dateScale),
            formatTwoDecimalPlaces(row.purchase),
            formatTwoDecimalPlaces(row.propertyTax),
            formatTwoDecimalPlaces(row.salesTax),
            formatTwoDecimalPlaces(row.partsCost),
            formatTwoDecimalPlaces(row.serviceCost),
            formatTwoDecimalPlaces(row.totalCost)
        ]);
    const footer = Object.keys(totals).length ? [
        `${dataTableFormatTime(totals.dateSelect, totals.dateScale)} ${translations.ONLINK_TOTAL}`,
        formatTwoDecimalPlaces(totals.purchase),
        formatTwoDecimalPlaces(totals.propertyTax),
        formatTwoDecimalPlaces(totals.salesTax),
        formatTwoDecimalPlaces(totals.partsCost),
        formatTwoDecimalPlaces(totals.serviceCost),
        formatTwoDecimalPlaces(totals.totalCost)
    ] : undefined;

    return {
        footer,
        header,
        rows
    };
}

export function getEquipmentUptimeExportContent(rollupData, headerName, translations) {
    const {
        rollupsForSelector = [], totals
    } = rollupData;
    const header = [
        headerName,
        translations.ONLINK_UPTIME,
        translations.ONLINK_DOWNTIME,
        translations.ONLINK_PERCENT
    ];
    const rows = [...rollupsForSelector]
        .sort((a, b) => alphaNumericCompare(a.name, b.name))
        .map((row) => [
            row.name,
            formatTwoDecimalPlaces(row.uptime),
            formatTwoDecimalPlaces(row.downtime),
            `${formatTwoDecimalPlaces(row.ratio)} ${UNITS.percent}`
        ]);

    if (!totals) {
        return {
            header,
            rows
        };
    }

    return {
        footer: [
            `${dataTableFormatTime(totals.dateSelect, totals.dateScale)} ${translations.ONLINK_TOTAL}`,
            formatTwoDecimalPlaces(totals.uptime),
            formatTwoDecimalPlaces(totals.downtime),
            `${formatTwoDecimalPlaces(totals.ratio)} ${UNITS.percent}`
        ],
        header,
        rows
    };
}

export function getLifetimeExportContent(lifetimeData, primarySelector, translations) {
    if (primarySelector === INVENTORIES) {
        return {
            header: [translations.ONLINK_PART_TYPE, translations.ONLINK_TOTAL_COST],
            rows: [...lifetimeData]
                .sort((a, b) => alphaNumericCompare(a.name, b.name))
                .map((row) => [row.partType, row.formattedTotalCost])
        };
    }

    return {
        header: [
            translations.NAME,
            translations.BRAND,
            translations.MODEL,
            translations.ONLINK_JOINED,
            translations.ONLINK_PURCHASE,
            translations.ONLINK_TAX,
            translations.PARTS,
            translations.ONLINK_SERVICE,
            translations.ONLINK_TOTAL,
            translations.HOURS,
            translations.ONLINK_COST_PER_HOUR
        ],
        rows: [...lifetimeData]
            .sort((a, b) => defaultSortMethod(a.name, b.name))
            .map((row) => [
                row.name,
                row.brand,
                row.model,
                row.joined,
                row.purchaseCostFormatted,
                row.taxFormatted,
                row.partsFormatted,
                row.serviceFormatted,
                row.totalFormatted,
                row.engineHoursFormatted,
                row.costPerEngineHourFormatted
            ])
    };
}

export function getServiceHistoryExportContent(rollupData, translations) {
    const header = [
        translations.DATE,
        translations.ONLINK_ASSIGNEES,
        translations.EQUIPMENT_LIST,
        translations.MODEL,
        translations.ONLINK_GROUP,
        translations.ONLINK_SERVICE,
        replaceTranslationNames(translations.DURATION_WITH_UNIT, {
            '0': translations.mins
        }),
        translations.HOURS
    ];
    const rows = [...rollupData]
        .sort((a, b) => moment(b.unFormattedTime).diff(moment(a.unFormattedTime)))
        .map((row) => [
            row.formattedTime,
            row.assignees,
            row.equipmentName,
            row.modelAndManufacturerName,
            row.serviceGroup,
            row.serviceTypeName,
            row.actualDuration,
            accounting.formatNumber(row.totalHours, 1)
        ]);

    return {
        header,
        rows
    };
}

export function getWorkboardJobListExportContent(workboardReport, translations) {
    const header = [
        translations.OPERATOR,
        translations.DATE,
        translations.ONLINK_WORKBOARD,
        translations.jobs_job,
        translations.IWP_EQUIPMENT_LABEL,
        translations.ONLINK_ROUTE,
        translations.ONLINK_MOWING_DIRECTIONS,
        translations.ONLINK_EST_HRS,
        translations.ONLINK_ACT_HRS,
        translations.ONLINK_PRE_START,
        translations.NOTE
    ];
    const rows = workboardReport.map((job) => [
        job.operator,
        job.formattedDate,
        job.workboardTitle,
        job.title,
        job.equipmentNames,
        job.route,
        job.mowingDirection,
        job.estDuration,
        job.actualDuration,
        job.formattedPreStartCheck,
        job.operatorNote
    ]);

    return {
        header,
        rows
    };
}

export function getLaborWorkboardsExportContent({
    columnHeaderName,
    currencyPreference,
    includeDate,
    translations,
    workboardsReport,
    hasViewFinancialDataPermission
}) {
    const dateHeader = includeDate ? [translations.DATE] : [];
    const header = hasViewFinancialDataPermission ?
        [
            columnHeaderName,
            ...dateHeader,
            translations.ONLINK_EST_LABOR_HOURS,
            translations.ONLINK_ESTIMATED_COST,
            translations.ONLINK_ACTUAL_HOURS,
            translations.ONLINK_ACTUAL_COST,
            translations.ONLINK_OVERTIME_HOURS,
            translations.ONLINK_OVERTIME_COST
        ] :
        [
            columnHeaderName,
            ...dateHeader,
            translations.ONLINK_EST_LABOR_HOURS,
            translations.ONLINK_ACTUAL_HOURS,
            translations.ONLINK_OVERTIME_HOURS
        ];
    const rows = workboardsReport.map((row) => {
        const rowDate = includeDate ? [dataTableFormatTime(row.dateSelect, row.dateScale)] : [];
        const baseValues = [
            row.name,
            ...rowDate,
            row.estDuration
        ];

        if (hasViewFinancialDataPermission) {
            return [
                ...baseValues,
                row.estLaborCost,
                row.actualDuration,
                row.laborCost,
                row.overtimeDuration,
                row.overtimeCost
            ];
        }

        return [
            ...baseValues,
            row.actualDuration,
            row.overtimeDuration
        ];
    });

    return {
        currencyPreference,
        header,
        rows
    };
}

function createFooterRow({
    dateSelect,
    timeScale,
    tertiarySelector,
    total,
    translations
}) {
    return [
        `${dataTableFormatTime(dateSelect, timeScale)} ${translations.ONLINK_TOTAL} - ${tertiarySelector}`,
        total
    ];
}

export function getLaborByStatusExportContent({
    dateSelect,
    laborData,
    tertiarySelector,
    timeScale,
    translations
}) {
    const rows = laborData.map((row) => [
        row.name,
        row.optionMap[tertiarySelector]
    ]);
    const total = laborData.reduce((totalValue, {optionMap}) => totalValue + optionMap[tertiarySelector], 0);

    return {
        footer: createFooterRow({
            dateSelect,
            timeScale,
            tertiarySelector,
            total,
            translations
        }),
        header: [translations.ONLINK_EMPLOYEE, translations.ONLINK_TOTAL],
        rows
    };
}

export function getLaborByEmployeeExportContent({
    dateSelect,
    laborData,
    tertiarySelector,
    timeScale,
    translations
}) {
    const [filteredData] = laborData.filter((data) => data.name === tertiarySelector);
    const rows = filteredData?.optionMap ? Object.entries(filteredData.optionMap) : [];
    const total = rows.reduce((totalValue, [, current]) => totalValue + current, 0);

    return {
        footer: createFooterRow({
            dateSelect,
            timeScale,
            tertiarySelector,
            total,
            translations
        }),
        header: [translations.STATUS, translations.ONLINK_TOTAL],
        rows
    };
}
